/**
 * RequestOverviewForm - Reusable form for Request overview fields
 * 
 * Used in:
 * - RequestEditModal (edit mode)
 * - RequestMergePanel (source view + merged form)
 * 
 * Props:
 * - formData: { type, title, description, stage, consideration_status, request_data }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states)
 * - colors: theme colors from useSafeMode
 * - errors: { [field]: string } - validation errors to display
 * - mode: 'edit' | 'create' | 'view'
 * - showStatus: boolean - whether to show stage/consideration_status fields
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { 
  AlertCircle, 
  Bug, 
  Sparkles, 
  Layers, 
  Rocket, 
  Target, 
  Users,
  FileText,
} from 'lucide-react';

// Required fields for request
export const REQUEST_REQUIRED_FIELDS = ['title'];

// Type configuration - Order: Bug, Feature, Epic, Initiative, Goal, Persona
export const REQUEST_TYPE_CONFIG = {
  bug: { color: 'bg-red-500', icon: Bug, label: 'Bug' },
  feature: { color: 'bg-blue-500', icon: Sparkles, label: 'Feature' },
  epic: { color: 'bg-purple-500', icon: Layers, label: 'Epic' },
  initiative: { color: 'bg-orange-500', icon: Rocket, label: 'Initiative' },
  goal: { color: 'bg-yellow-500', icon: Target, label: 'Goal' },
  persona: { color: 'bg-green-500', icon: Users, label: 'Persona' },
};

// Priority options
export const REQUEST_PRIORITY_OPTIONS = [
  { value: 'Critical', label: 'Critical', color: 'text-red-600' },
  { value: 'High', label: 'High', color: 'text-orange-600' },
  { value: 'Medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'Low', label: 'Low', color: 'text-green-600' },
];

// Stage options
export const REQUEST_STAGE_OPTIONS = [
  { value: 'new', label: 'Inbox (New)' },
  { value: 'considering', label: 'Considering' },
  { value: 'rejected', label: 'Rejected' },
];

// Consideration status options
export const REQUEST_CONSIDERATION_OPTIONS = [
  { value: 'needs_definition', label: 'Needs Definition' },
  { value: 'ready_to_rank', label: 'Ready to Rank' },
  { value: 'ready_to_commit', label: 'Ready to Commit' },
];

const RequestOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  errors = {},
  mode = 'edit',
  showStatus = false,
}) => {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  const handlePriorityChange = (value) => {
    if (onChange) {
      onChange('request_data', { 
        ...(formData.request_data || {}), 
        priority_level: value 
      });
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  const effectiveDisabled = isDisabled || !isEditing;
  const typeConfig = REQUEST_TYPE_CONFIG[formData.type] || REQUEST_TYPE_CONFIG.feature;
  const TypeIcon = typeConfig.icon;

  return (
    <div className="space-y-4">
      {/* Type and Priority Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Type Selection */}
        <div>
          <Label className={cn("flex items-center gap-1", colors?.textSecondary)}>
            <FileText className="w-3 h-3" />
            Type
          </Label>
          {isEditing && !isDisabled ? (
            <Select 
              value={formData.type || 'feature'} 
              onValueChange={(v) => handleChange('type', v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger 
                className={cn("mt-1", colors?.bgInput, colors?.borderPrimary)}
                data-testid="request-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(REQUEST_TYPE_CONFIG).map(([type, config]) => (
                  <SelectItem key={type} value={type}>
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-2 rounded-full", config.color)} />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <div className={cn("w-3 h-3 rounded-full", typeConfig.color)} />
              <span className={colors?.textPrimary}>{typeConfig.label}</span>
            </div>
          )}
        </div>

        {/* Priority */}
        <div>
          <Label className={colors?.textSecondary}>Priority</Label>
          {isEditing && !isDisabled ? (
            <Select 
              value={formData.request_data?.priority_level || 'Medium'} 
              onValueChange={handlePriorityChange}
              disabled={effectiveDisabled}
            >
              <SelectTrigger 
                className={cn("mt-1", colors?.bgInput, colors?.borderPrimary)}
                data-testid="request-priority-select"
              >
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className={option.color}>{option.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn(
              "mt-1",
              REQUEST_PRIORITY_OPTIONS.find(p => p.value === formData.request_data?.priority_level)?.color || colors?.textSecondary
            )}>
              {formData.request_data?.priority_level || 'Medium'}
            </p>
          )}
        </div>
      </div>

      {/* Title */}
      <div>
        <Label className={cn(
          "flex items-center gap-1",
          colors?.textSecondary,
          errors.title && "text-red-500"
        )}>
          Title
          <span className="text-red-500">*</span>
        </Label>
        {isEditing && !isDisabled ? (
          <>
            <Input
              value={formData.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter request title..."
              disabled={effectiveDisabled}
              className={cn(
                "mt-1",
                colors?.bgInput,
                colors?.borderPrimary,
                colors?.textPrimary,
                getFieldErrorClass('title')
              )}
              data-testid="request-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.title}
              </p>
            )}
          </>
        ) : (
          <p className={cn("mt-1 text-lg font-medium", colors?.textPrimary)}>
            {formData.title || 'Untitled Request'}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <Label className={colors?.textSecondary}>Description</Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe the request in detail..."
            rows={5}
            disabled={effectiveDisabled}
            className={cn(
              "mt-1 min-h-[120px]",
              colors?.bgInput,
              colors?.borderPrimary,
              colors?.textPrimary,
              getFieldErrorClass('description')
            )}
            data-testid="request-description-input"
          />
        ) : (
          <div className={cn(
            "mt-1 p-3 rounded-lg min-h-[80px] whitespace-pre-wrap",
            colors?.bgTertiary
          )}>
            <p className={cn("text-sm", colors?.textSecondary)}>
              {formData.description || <span className={colors?.textMuted}>No description</span>}
            </p>
          </div>
        )}
      </div>

      {/* Status Fields - Only shown when showStatus is true */}
      {showStatus && (
        <div className="grid grid-cols-2 gap-4">
          {/* Stage */}
          <div>
            <Label className={colors?.textSecondary}>Status</Label>
            {isEditing && !isDisabled ? (
              <Select 
                value={formData.stage || 'new'} 
                onValueChange={(v) => handleChange('stage', v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger 
                  className={cn("mt-1", colors?.bgInput, colors?.borderPrimary)}
                  data-testid="request-status-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_STAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className={cn("mt-1", colors?.textSecondary)}>
                {REQUEST_STAGE_OPTIONS.find(s => s.value === formData.stage)?.label || 'New'}
              </p>
            )}
          </div>

          {/* Consideration Status - Only when stage is 'considering' */}
          {formData.stage === 'considering' && (
            <div>
              <Label className={colors?.textSecondary}>Board Column</Label>
              {isEditing && !isDisabled ? (
                <Select 
                  value={formData.consideration_status || 'needs_definition'} 
                  onValueChange={(v) => handleChange('consideration_status', v)}
                  disabled={effectiveDisabled}
                >
                  <SelectTrigger 
                    className={cn("mt-1", colors?.bgInput, colors?.borderPrimary)}
                    data-testid="request-column-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_CONSIDERATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={cn("mt-1", colors?.textSecondary)}>
                  {REQUEST_CONSIDERATION_OPTIONS.find(c => c.value === formData.consideration_status)?.label || 'Needs Definition'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RequestOverviewForm;
